import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Page, Steps } from "../../../helbest";
import HeaderBack from "../../../helbest/HeaderBack/HeaderBack";

import CartItem from "../../components/CartItem/CartItem";
import EmptyCart from "../Cart/EmptyCart/EmptyCart";
import Summery from "../Cart/Summery/Summery";
import Loading from "../../components/Loading/Loading";

import { useCheckoutQuery, useProductQuery } from "../../../saleor/api";
import { calculator } from "../../../base/utils";
import { CartItemTransformer } from "../../../services/objectTransformer/cart";
import { _Cart_Update } from "../../../store/actions/cart_actions";

import { useFastCheckoutParams } from "../../../hooks/useFastCheckoutParams";
import { useFakeCartFromProduct } from "../../../hooks/useFakeCartFromProduct";
import { useAutoCheckoutCreate } from "../../../hooks/useAutoCheckoutCreate";
import FastCheckoutFooter from "./FastCheckoutFooter";
import { useFastCheckoutLineSync } from "./useFastCheckoutLineSync";

const FastCheckout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const Login = useSelector((state) => state.Login);
  const Cart = useSelector((state) => state.Cart);
  const hasSeededGuestCart = useRef(false);
  const { t, i18n } = useTranslation();

  const {
    productId,
    variantId,
    quantity: urlQuantity,
    channel: urlChannel,
    cid,
    cpid,
    ready,
  } = useFastCheckoutParams();

  // set channel
  useEffect(() => {
    if (urlChannel) localStorage.setItem("channel", urlChannel);
    else if (!localStorage.getItem("channel"))
      localStorage.setItem("channel", "default-channel");
  }, [urlChannel]);

  const channel = localStorage.getItem("channel");

  const { data: productData, loading: productLoading } = useProductQuery({
    variables: { id: productId, channel, lng: i18n.language.toUpperCase() },
    skip: !productId || !channel,
  });

  const selectedVariantId = useMemo(() => {
    if (!productData?.product) return null;
    return variantId || productData.product.variants?.[0]?.id || null;
  }, [productData, variantId]);

  const { cart: urlCart, tax: urlTax } = useFakeCartFromProduct({
    productData,
    variantId: selectedVariantId,
    quantity: urlQuantity,
  });

  const {
    data: checkoutData,
    loading: checkoutLoading,
    refetch: checkoutRefetch,
  } = useCheckoutQuery({
    variables: { id: Login.checkoutId },
    skip: !Login.token || !Login.checkoutId,
  });

  const pending = useMemo(() => {
    if (!Login.token)
      return JSON.parse(localStorage.getItem("pendingFastChackout") || "{}");
    return null;
  }, [Login.token]);

  const { syncingFastLine, fastCheckoutApplied } = useFastCheckoutLineSync({
    isLoggedIn: Boolean(Login.token),
    checkoutId: Login.checkoutId,
    selectedVariantId,
    channel,
    quantity: urlQuantity,
    checkoutLoading,
    checkoutData,
    checkoutRefetch,
  });

  const shouldCreateCheckout = Boolean(
    !fastCheckoutApplied &&
    ((ready &&
      Login.token &&
      productId &&
      !Login.checkoutId &&
      productData?.product &&
      selectedVariantId &&
      channel) ||
      (Login.token && pending?.variantId)),
  );

  const { loading: isCreatingCheckout, noGatewaysAvailable } = useAutoCheckoutCreate({
    enabled: shouldCreateCheckout,
    variantId: pending?.variantId || selectedVariantId,
    quantity: pending?.quantity || urlQuantity,
    channel: pending?.channel || channel,
    cid: pending?.cid || cid,
    cpid: pending?.cpid || cpid,
    language: i18n.language.toUpperCase(),
    checkoutRefetch,
  });

  const cart = useMemo(() => {
    if (!Login.token) return Cart;
    return (
      checkoutData?.checkout?.lines?.map((line) => CartItemTransformer(line)) ||
      []
    );
  }, [Login.token, Cart, checkoutData]);

  useEffect(() => {
    if (
      !Login.token &&
      !hasSeededGuestCart.current &&
      Cart.length === 0 &&
      urlCart.length > 0
    ) {
      dispatch(_Cart_Update(urlCart));
      calculator();
      hasSeededGuestCart.current = true;
    }
  }, [Login.token, Cart.length, urlCart, dispatch]);

  useEffect(() => {
    if (Login.token) {
      dispatch(_Cart_Update(cart));
      calculator();
    }
  }, [Login.token, cart, dispatch]);

  const handleNext = () => {
    if (!Login.token) {
      localStorage.setItem(
        "pendingFastChackout",
        JSON.stringify({
          productId,
          variantId: selectedVariantId,
          quantity: urlQuantity,
          channel,
          cid,
          cpid,
        }),
      );
      navigate("/login?redirect=/fast-checkout");
      return;
    }

    if (isCreatingCheckout || cart.length === 0) return;

    navigate("/delivery/");
  };

  const loading =
    productLoading || checkoutLoading || isCreatingCheckout || syncingFastLine;

  return (
    <Page
      className="page-cart bg-gray-light"
      title={t("common.cart")}
      footer={
        <FastCheckoutFooter
          onNext={handleNext}
          disabled={loading || cart.length === 0}
        />
      }
      showFooter
      customHeader={<HeaderBack title={t("common.cart")} url="/" />}
      headerFix
      extra_header={<Steps step={1} hasShadow />}
      logo
      back
      address="/"
    >
      {loading ? (
        <div className="text-center mt-5">
          <Loading />
        </div>
      ) : cart.length === 0 ? (
        <EmptyCart />
      ) : (
        <section className="cart-page-body">
          <div className="cart-item-container">
            {cart.map((item, index) =>
              item?.product ? (
                <CartItem
                  key={item.id || index}
                  data={item.product}
                  checkoutLineId={item.id}
                  checkoutRefetch={checkoutRefetch}
                />
              ) : null,
            )}

            <Summery
              tax={
                !Login.token ? urlTax : checkoutData?.checkout?.totalPrice?.tax
              }
            />
          </div>
        </section>
      )}
    </Page>
  );
};

export default FastCheckout;
